import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {MatrixEvent} from 'matrix-js-sdk';
import {
  extractEmailPart,
  getProperMailAddress
} from '@/components/matrix-chat/utils/helper-functions';
import ResponsiveTypography from '@/components/ui/responsive-typography';
import UserOverview, {UserInOverview} from '@/components/user/overview';
import {Box, Tooltip} from '@mui/material';

import {getUserByMail} from '@/rest/user/get-user-by-mail';
import {useMatrixRoomContext} from '@/contexts/matrix/room';
import {useAppSelector} from '@/redux-store/store';
import {useDispatch} from 'react-redux';
import {setUserData} from '@/redux-store/slices/matrix/user-data';
import {getText} from '@/services/matrix/messages';
import {useMatrixContext} from '@/contexts/matrix/client';
import {isDirectChat} from '@/services/matrix/room';

interface SystemMatrixEventProps {
  content: MatrixEvent;
}

const SystemMessage: React.FC<SystemMatrixEventProps> = ({content}) => {
  const [userOverviewDialogOpen, setUserOverviewDialogOpen] = useState(false);
  const [selectedUserToOverview, setSelectedUserToOverview] =
    useState<UserInOverview | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [isDirect, setIsDirect] = useState(false);

  const {t} = useTranslation('system_messages');

  const {handleOpenUserDialog, handleCloseUserDialog, currentMatrixRoomId} =
    useMatrixRoomContext();

  const {matrixClient} = useMatrixContext();

  const dispatch = useDispatch();

  const effectiveMatrixEvent = content.getEffectiveEvent();

  const senderId = effectiveMatrixEvent.state_key;

  const userMail = useMemo(() => {
    const extractedMail = extractEmailPart(senderId);
    if (extractedMail) {
      const userMail = getProperMailAddress(extractedMail);
      return userMail;
    }
  }, [senderId]);

  const userData = useAppSelector(
    (store) => store.matrixUser.userDataByEmail[userMail ?? '']
  );

  const handleCheckIfRoomIsDirect = useCallback(async () => {
    if (matrixClient && currentMatrixRoomId) {
      return await isDirectChat(matrixClient, currentMatrixRoomId);
    }
    return false;
  }, [currentMatrixRoomId, matrixClient]);

  const handleUserOverviewClick = useCallback(
    (userId: string) => {
      getUserByMail(userId)
        .then((fetchedUser) => {
          setSelectedUserToOverview(fetchedUser);
          setUserOverviewDialogOpen(true);
          handleOpenUserDialog();
        })
        .catch((error) => {
          console.error('Failed to fetch user', error);
        });
    },
    [handleOpenUserDialog]
  );

  const handleUserOverviewClose = useCallback(() => {
    setSelectedUserToOverview(null);
    setUserOverviewDialogOpen(false);
    handleCloseUserDialog();
  }, [handleCloseUserDialog]);

  const messageText = useMemo(() => {
    let message: string | null = null;
    message = getText(content, t, userName || undefined, isDirect);

    return message;
  }, [content, isDirect, t, userName]);

  useEffect(() => {
    const checkIfRoomDirect = async () => {
      try {
        const isDirect = await handleCheckIfRoomIsDirect();
        setIsDirect(isDirect);
      } catch (error) {
        console.error('Failed to check if room is direct:', error);
      }
    };

    checkIfRoomDirect();
  }, [content, t, userName, handleCheckIfRoomIsDirect]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (userMail && !userData) {
          const fetchedData = await getUserByMail(userMail);
          if (fetchedData) {
            dispatch(setUserData({email: userMail, data: fetchedData}));
            setUserName(fetchedData.name);
          }
        } else if (userData && userData.name) {
          setUserName(userData.name);
        }
      } catch (error) {
        console.error('Failed to get user data', error);
      }
    };

    fetchData();
  }, [userMail, userData, dispatch]);

  if (!messageText || userName?.includes('admin=40sportjoe.net')) {
    return null;
  }
  return (
    <>
      <Tooltip title={t('user_profile')}>
        <Box
          display="flex"
          width="100%"
          justifyContent="center"
          alignItems="center"
        >
          <ResponsiveTypography
            onClick={() => {
              if (userMail) {
                handleUserOverviewClick(userMail);
              }
            }}
            smallAllSizes
            sx={{
              margin: 0.5,
              color: 'primary',
              cursor: 'pointer'
            }}
          >
            {messageText}
          </ResponsiveTypography>
        </Box>
      </Tooltip>
      <UserOverview
        userData={selectedUserToOverview}
        userViewDialogOpen={userOverviewDialogOpen}
        onClose={handleUserOverviewClose}
      />
    </>
  );
};

export default SystemMessage;
